import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType,
  Header, Footer, PageNumber
} from 'docx'
import { saveAs } from 'file-saver'
import { resolveCarrierGroup } from './resolveCarrierGroup'

// Insurance Review Summary DOCX exporter.
// Produces an advisor-synthesized, non-sensitive deliverable for client share-out
// or for use as the Section 09 source in the Executive Advisory Report.
//
// Single-policy and portfolio (stacked-coverage) layouts.

const DEFAULT_BRAND = {
  firmName: 'Sidewalk Security Advisors',
  logoUrl: null,
  primaryHex: '0D1B2A',
  accentHex: 'C9A84C',
  trademarkLine: null,
  firmAddress: '200 Missionary Rdg STE 319, Birmingham, AL 35242',
  authorName: 'Jeremy Krienke',
  authorTitle: 'Principal Advisor',
  firmLegalName: 'Sidewalk Security Advisors LLC',
  font: 'Aptos'
}

const CW = 10080
const SP6 = 120

export async function exportInsuranceReviewDocx({ client, review, brand = DEFAULT_BRAND }) {
  const b = { ...DEFAULT_BRAND, ...brand }
  const P = strip(b.primaryHex)
  const A = strip(b.accentHex)
  const F = b.font || DEFAULT_BRAND.font
  const firm = b.firmName || DEFAULT_BRAND.firmName
  const clientName = client?.name || 'Client Organization'
  const dateStr = new Date(review?.reviewed_at || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })

  const isPortfolio = review?.review_type === 'portfolio'
  const reportLabel = isPortfolio ? 'Cyber Insurance Portfolio Review' : 'Cyber Insurance Policy Review'

  const cover = buildCover({ b, F, P, A, firm, clientName, dateStr, reportLabel, review })
  const body = isPortfolio
    ? buildPortfolioBody({ b, F, P, A, review })
    : buildSingleBody({ b, F, P, A, review })
  const meta = buildReviewMetadataBlock({ F, P, review })

  const doc = new Document({
    styles: { default: { document: { run: { font: F, size: 22 } } } },
    sections: [{
      properties: {
        page: { size: { width: 12240, height: 15840 }, margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } }
      },
      headers: {
        default: new Header({
          children: [new Table({
            width: { size: CW, type: WidthType.DXA },
            columnWidths: [Math.floor(CW * 0.6), Math.floor(CW * 0.4)],
            borders: noBd(),
            rows: [new TableRow({ children: [
              new TableCell({
                width: { size: Math.floor(CW * 0.6), type: WidthType.DXA },
                borders: noBd(),
                children: [new Paragraph({ children: [new TextRun({ text: b.firmAddress || '', font: F, size: 14, color: '64748B' })] })]
              }),
              new TableCell({
                width: { size: Math.floor(CW * 0.4), type: WidthType.DXA },
                borders: noBd(),
                children: [new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: '[LOGO] ', font: F, size: 14, bold: true, color: P }),
                    new TextRun({ text: firm.toLowerCase(), font: F, size: 16, color: P })
                  ]
                })]
              })
            ]})]
          })]
        })
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            tabStops: [{ type: 'right', position: 9360 }],
            children: [
              new TextRun({ text: `${clientName} ${reportLabel}`, font: F, size: 16, color: '64748B' }),
              new TextRun({ text: '\t| ', font: F, size: 16, color: '64748B' }),
              new TextRun({ children: [PageNumber.CURRENT], font: F, size: 16, color: '64748B' })
            ]
          })]
        })
      },
      children: [...cover, pageBreak(), ...body, ...meta]
    }]
  })

  const blob = await Packer.toBlob(doc)
  const safeName = clientName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  const dateSlug = new Date(review?.reviewed_at || Date.now()).toISOString().slice(0, 10)
  const fileTag = isPortfolio ? 'portfolio' : 'policy'
  saveAs(blob, `${safeName}-insurance-${fileTag}-review-${dateSlug}.docx`)
}

// ============================================================
// Cover
// ============================================================

function buildCover({ b, F, P, A, firm, clientName, dateStr, reportLabel, review }) {
  const blocks = []

  // Header band with logo placeholder slot
  blocks.push(new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [Math.floor(CW * 0.25), Math.floor(CW * 0.75)],
    rows: [new TableRow({ cantSplit: true, children: [
      new TableCell({
        width: { size: Math.floor(CW * 0.25), type: WidthType.DXA },
        shading: { fill: P, type: ShadingType.CLEAR },
        borders: noBd(),
        margins: { top: 240, bottom: 240, left: 240, right: 240 },
        verticalAlign: 'center',
        children: [new Paragraph({
          alignment: AlignmentType.CENTER,
          children: [new TextRun({ text: '[LOGO PLACEHOLDER]', font: F, size: 18, bold: true, color: A })]
        })]
      }),
      new TableCell({
        width: { size: Math.floor(CW * 0.75), type: WidthType.DXA },
        shading: { fill: P, type: ShadingType.CLEAR },
        borders: noBd(),
        margins: { top: 240, bottom: 240, left: 280, right: 280 },
        children: [
          new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: firm.toUpperCase(), font: F, size: 20, bold: true, color: A })] }),
          new Paragraph({ children: [new TextRun({ text: reportLabel.toUpperCase(), font: F, size: 16, color: 'FFFFFF' })] })
        ]
      })
    ]})]
  }))
  blocks.push(gap(400))

  blocks.push(new Paragraph({
    children: [new TextRun({ text: reportLabel, font: F, size: 56, bold: true, color: P })],
    spacing: { after: 200 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: b.firmLegalName || 'Sidewalk Security Advisors LLC', font: F, size: 24, bold: true, color: '2D3748' })],
    spacing: { after: 80 }
  }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: dateStr.toUpperCase(), font: F, size: 22, italics: true, color: P })],
    spacing: { after: 400 }
  }))

  blocks.push(labeledLine({ F, label: 'Prepared for:', value: clientName }))
  if (review?.policy_label) blocks.push(labeledLine({ F, label: 'Policy:', value: review.policy_label, italics: true }))
  blocks.push(gap(200))
  blocks.push(labeledLine({ F, label: 'Created by:', value: `${b.authorName || 'Jeremy Krienke'} – ${b.authorTitle || 'Principal Advisor'}` }))
  blocks.push(new Paragraph({
    children: [new TextRun({ text: b.firmLegalName || 'Sidewalk Security Advisors LLC', font: F, size: 20, italics: true, color: '64748B' })]
  }))

  return blocks
}

function labeledLine({ F, label, value, italics = false }) {
  return new Paragraph({
    spacing: { after: 60 },
    children: [
      new TextRun({ text: `${label} `, font: F, size: 22, bold: true, color: '2D3748' }),
      new TextRun({ text: value, font: F, size: 22, italics, color: '2D3748' })
    ]
  })
}

// ============================================================
// Single-policy body
// ============================================================

function buildSingleBody({ b, F, P, A, review }) {
  const blocks = []
  const f = review?.findings || {}
  const summary = f.policySummary || {}

  blocks.push(sectionHeader({ F, P, A, title: 'Policy Summary' }))
  blocks.push(buildPolicySummaryTable({ F, P, summary, review }))

  if (Array.isArray(summary.coverageSections) && summary.coverageSections.length > 0) {
    blocks.push(subHeader({ F, P, title: 'Coverage Sections' }))
    blocks.push(bulletList({ F, items: summary.coverageSections }))
  }

  if (Array.isArray(summary.notableExclusions) && summary.notableExclusions.length > 0) {
    blocks.push(subHeader({ F, P, title: 'Notable Exclusions' }))
    blocks.push(bulletList({ F, items: summary.notableExclusions }))
  }

  if (Array.isArray(summary.endorsements) && summary.endorsements.length > 0) {
    blocks.push(subHeader({ F, P, title: 'Endorsements' }))
    blocks.push(bulletList({ F, items: summary.endorsements }))
  }

  if (summary.sublimits && Object.keys(summary.sublimits).length > 0) {
    blocks.push(subHeader({ F, P, title: 'Sublimits' }))
    blocks.push(buildKeyValueTable({ F, P, items: summary.sublimits, valueFormatter: formatCurrency }))
  }

  if (Array.isArray(f.strengths) && f.strengths.length > 0) {
    blocks.push(sectionHeader({ F, P, A, title: 'Strengths' }))
    f.strengths.forEach(s => {
      blocks.push(new Paragraph({
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({ text: '✓ ', bold: true, color: '15803D', font: F, size: 22 }),
          new TextRun({ text: s.title, bold: true, color: '2D3748', font: F, size: 22 })
        ]
      }))
      if (s.detail) blocks.push(bodyParagraph({ F, text: s.detail, indent: { left: 300 } }))
    })
  }

  if (Array.isArray(f.gaps) && f.gaps.length > 0) {
    blocks.push(sectionHeader({ F, P, A, title: 'Coverage Gaps' }))
    severityGroupedBlocks({ F, P, items: f.gaps, severityKey: 'severity', includeBusinessImpact: true }).forEach(b => blocks.push(b))
  }

  if (Array.isArray(f.recommendations) && f.recommendations.length > 0) {
    blocks.push(sectionHeader({ F, P, A, title: 'Recommendations' }))
    severityGroupedBlocks({ F, P, items: f.recommendations, severityKey: 'priority', includeRationale: true }).forEach(b => blocks.push(b))
  }

  return blocks
}

function buildPolicySummaryTable({ F, P, summary, review }) {
  const rows = []
  const cell = (label, value) => new TableRow({
    children: [
      new TableCell({
        width: { size: Math.floor(CW * 0.35), type: WidthType.DXA },
        shading: { fill: 'F1F5F9', type: ShadingType.CLEAR },
        borders: hBd('E2E8F0'),
        margins: { top: 60, bottom: 60, left: 80, right: 60 },
        children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, color: P, font: F, size: 20 })] })]
      }),
      new TableCell({
        width: { size: Math.floor(CW * 0.65), type: WidthType.DXA },
        shading: { fill: 'FFFFFF', type: ShadingType.CLEAR },
        borders: hBd('E2E8F0'),
        margins: { top: 60, bottom: 60, left: 80, right: 60 },
        children: [new Paragraph({ children: [new TextRun({ text: value || 'Not provided', font: F, size: 20, color: '2D3748' })] })]
      })
    ]
  })

  const carrierName = summary.carrier || review?.carrier
  const carrierGroup = resolveCarrierGroup(carrierName)
  rows.push(cell('Carrier', carrierName ? (carrierGroup?.brand ? `${carrierName} (${carrierGroup.brand})` : carrierName) : null))
  rows.push(cell('Coverage Limit (per event)', formatCurrency(summary.coverageLimitPerEvent ?? review?.coverage_limit_per_event)))
  if (summary.coverageLimitAggregate != null || review?.coverage_limit_aggregate != null) {
    rows.push(cell('Aggregate Limit', formatCurrency(summary.coverageLimitAggregate ?? review?.coverage_limit_aggregate)))
  }
  rows.push(cell('Retention', formatCurrency(summary.retention ?? review?.retention)))
  if (summary.waitingPeriodHours != null || review?.waiting_period_hours != null) {
    rows.push(cell('Waiting Period', `${summary.waitingPeriodHours ?? review?.waiting_period_hours} hours`))
  }
  if (summary.effectiveDate || review?.effective_date) rows.push(cell('Effective Date', summary.effectiveDate || review?.effective_date))
  if (summary.expirationDate || review?.expiration_date) rows.push(cell('Expiration Date', summary.expirationDate || review?.expiration_date))

  return new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [Math.floor(CW * 0.35), Math.floor(CW * 0.65)],
    rows
  })
}

// ============================================================
// Portfolio body
// ============================================================

function buildPortfolioBody({ b, F, P, A, review }) {
  const blocks = []
  const f = review?.findings || {}
  const summary = f.portfolioSummary || {}
  const cross = f.crossLayerFindings || {}

  blocks.push(sectionHeader({ F, P, A, title: 'Portfolio Summary' }))

  blocks.push(new Paragraph({
    spacing: { after: 120 },
    children: [
      new TextRun({ text: `Total aggregate coverage: `, bold: true, color: P, font: F, size: 22 }),
      new TextRun({ text: formatCurrency(summary.totalAggregateCoverage ?? review?.coverage_limit_aggregate), font: F, size: 22, color: '2D3748' }),
      new TextRun({ text: `   •   Layers: ${summary.layerCount || (review?.document_ids?.length || 0)}`, font: F, size: 22, color: '64748B' })
    ]
  }))

  if (Array.isArray(summary.layers) && summary.layers.length > 0) {
    blocks.push(subHeader({ F, P, title: 'Layer Structure' }))
    blocks.push(buildLayerTable({ F, P, layers: summary.layers }))
  }

  blocks.push(sectionHeader({ F, P, A, title: 'Cross-Layer Analysis' }))
  const dimensions = [
    { key: 'followFormConsistency', title: 'Follow-Form Consistency' },
    { key: 'erosionAndDropDown', title: 'Erosion & Drop-Down' },
    { key: 'sublimitCarryDown', title: 'Sublimit Carry-Down' },
    { key: 'retentionStructure', title: 'Retention Structure' },
    { key: 'consentPanelConflicts', title: 'Consent & Panel Counsel' }
  ]
  dimensions.forEach(d => {
    if (cross[d.key]) {
      blocks.push(new Paragraph({
        spacing: { before: 120, after: 40 },
        children: [new TextRun({ text: d.title, bold: true, color: P, font: F, size: 22 })]
      }))
      blocks.push(bodyParagraph({ F, text: cross[d.key] }))
    }
  })

  if (Array.isArray(cross.exclusionAsymmetry) && cross.exclusionAsymmetry.length > 0) {
    blocks.push(new Paragraph({
      spacing: { before: 120, after: 40 },
      children: [new TextRun({ text: 'Exclusion Asymmetry', bold: true, color: P, font: F, size: 22 })]
    }))
    cross.exclusionAsymmetry.forEach(e => {
      blocks.push(new Paragraph({
        spacing: { after: 40 },
        children: [
          new TextRun({ text: `[${(e.severity || 'medium').toUpperCase()}] `, bold: true, color: severityHex(e.severity), font: F, size: 18 }),
          new TextRun({ text: e.detail || '', font: F, size: 20, color: '2D3748' })
        ]
      }))
    })
  }

  if (Array.isArray(f.programGaps) && f.programGaps.length > 0) {
    blocks.push(sectionHeader({ F, P, A, title: 'Program Gaps' }))
    severityGroupedBlocks({ F, P, items: f.programGaps, severityKey: 'severity', includeBusinessImpact: true }).forEach(b => blocks.push(b))
  }

  if (Array.isArray(f.programRecommendations) && f.programRecommendations.length > 0) {
    blocks.push(sectionHeader({ F, P, A, title: 'Program Recommendations' }))
    severityGroupedBlocks({ F, P, items: f.programRecommendations, severityKey: 'priority', includeRationale: true }).forEach(b => blocks.push(b))
  }

  return blocks
}

function buildLayerTable({ F, P, layers }) {
  const headerCell = (label) => new TableCell({
    width: { size: Math.floor(CW * 0.2), type: WidthType.DXA },
    shading: { fill: P, type: ShadingType.CLEAR },
    borders: hBd(P),
    margins: { top: 60, bottom: 60, left: 80, right: 60 },
    children: [new Paragraph({ children: [new TextRun({ text: label, bold: true, color: 'FFFFFF', font: F, size: 18 })] })]
  })

  const dataCell = (text, alt) => new TableCell({
    width: { size: Math.floor(CW * 0.2), type: WidthType.DXA },
    shading: { fill: alt ? 'F8FAFC' : 'FFFFFF', type: ShadingType.CLEAR },
    borders: hBd('E2E8F0'),
    margins: { top: 50, bottom: 50, left: 80, right: 60 },
    children: [new Paragraph({ children: [new TextRun({ text: String(text ?? '—'), font: F, size: 19, color: '2D3748' })] })]
  })

  const rows = [new TableRow({ children: [
    headerCell('Layer'), headerCell('Carrier'), headerCell('Limit'), headerCell('Attachment'), headerCell('Follow-Form')
  ]})]

  layers.forEach((L, i) => {
    const alt = i % 2 === 0
    const group = resolveCarrierGroup(L.carrier)
    const carrierLabel = L.carrier
      ? (group?.brand ? `${L.carrier} (${group.brand})` : L.carrier)
      : '—'
    rows.push(new TableRow({ children: [
      dataCell(L.order || (i + 1), alt),
      dataCell(carrierLabel, alt),
      dataCell(formatCurrency(L.limit), alt),
      dataCell(formatCurrency(L.attachmentPoint), alt),
      dataCell(L.isFollowForm == null ? '—' : (L.isFollowForm ? 'Yes' : 'No'), alt)
    ]}))
  })

  return new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [Math.floor(CW * 0.1), Math.floor(CW * 0.3), Math.floor(CW * 0.2), Math.floor(CW * 0.2), Math.floor(CW * 0.2)],
    rows
  })
}

// ============================================================
// Severity-grouped findings
// ============================================================

function severityGroupedBlocks({ F, P, items, severityKey, includeBusinessImpact = false, includeRationale = false }) {
  const blocks = []
  const order = ['critical', 'high', 'medium', 'low']
  const grouped = {}
  for (const lvl of order) grouped[lvl] = []
  for (const item of items) {
    const key = String(item[severityKey] || 'medium').toLowerCase()
    ;(grouped[key] || grouped.medium).push(item)
  }

  order.forEach(lvl => {
    const list = grouped[lvl]
    if (!list || list.length === 0) return
    blocks.push(new Paragraph({
      spacing: { before: 200, after: 80 },
      children: [new TextRun({ text: `${lvl.toUpperCase()} (${list.length})`, bold: true, color: severityHex(lvl), font: F, size: 20 })]
    }))
    list.forEach(item => {
      blocks.push(new Paragraph({
        spacing: { before: 80, after: 30 },
        children: [
          new TextRun({ text: '• ', bold: true, color: P, font: F, size: 22 }),
          new TextRun({ text: item.title || 'Untitled', bold: true, color: '2D3748', font: F, size: 22 })
        ]
      }))
      if (item.detail) blocks.push(bodyParagraph({ F, text: item.detail, indent: { left: 300 } }))
      if (includeBusinessImpact && item.businessImpact) {
        blocks.push(new Paragraph({
          spacing: { after: 40 },
          indent: { left: 300 },
          shading: { fill: 'FEE2E2', type: ShadingType.CLEAR },
          children: [
            new TextRun({ text: 'Business Impact: ', bold: true, color: '991B1B', font: F, size: 19 }),
            new TextRun({ text: item.businessImpact, italics: true, color: '991B1B', font: F, size: 19 })
          ]
        }))
      }
      if (includeRationale && item.rationale) {
        blocks.push(new Paragraph({
          spacing: { after: 40 },
          indent: { left: 300 },
          children: [new TextRun({ text: item.rationale, italics: true, color: '64748B', font: F, size: 19 })]
        }))
      }
    })
  })

  return blocks
}

// ============================================================
// Metadata block (last page)
// ============================================================

function buildReviewMetadataBlock({ F, P, review }) {
  const blocks = [gap(300)]
  blocks.push(new Paragraph({
    spacing: { before: 200, after: 80 },
    border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'E2E8F0', space: 4 } },
    children: [new TextRun({ text: 'REVIEW METADATA', bold: true, color: '64748B', font: F, size: 16 })]
  }))

  const conf = review?.review_confidence?.overallConfidence
  const meta = [
    ['Review type', review?.review_type === 'portfolio' ? 'Portfolio (stacked coverage)' : 'Single policy'],
    ['Reviewed at', new Date(review?.reviewed_at || Date.now()).toLocaleString()],
    ['QA confidence', typeof conf === 'number' ? `${conf}%` : 'N/A'],
    ['PDF retention', review?.pdf_status === 'deleted' ? 'Deleted (findings persisted as non-sensitive summary)' : 'Retained']
  ]
  meta.forEach(([k, v]) => {
    blocks.push(new Paragraph({
      spacing: { after: 40 },
      children: [
        new TextRun({ text: `${k}: `, bold: true, color: '475569', font: F, size: 18 }),
        new TextRun({ text: String(v), color: '475569', font: F, size: 18 })
      ]
    }))
  })

  if (review?.review_notes) {
    blocks.push(new Paragraph({
      spacing: { before: 120, after: 40 },
      children: [new TextRun({ text: 'QA Notes', bold: true, color: P, font: F, size: 18 })]
    }))
    blocks.push(bodyParagraph({ F, text: review.review_notes, italics: true }))
  }

  blocks.push(new Paragraph({
    spacing: { before: 200 },
    children: [new TextRun({
      text: 'This document is advisor-synthesized. All identifying information from the underlying policy text was stripped during AI review and is not stored. Findings reflect the advisor\'s assessment as of the review date and should be re-evaluated when policies renew or organizational context changes.',
      italics: true,
      color: '94A3B8',
      font: F,
      size: 16
    })]
  }))

  return blocks
}

// ============================================================
// Primitives
// ============================================================

function sectionHeader({ F, P, A, title }) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 280, after: 200 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: A, space: 4 } },
    children: [new TextRun({ text: title, bold: true, size: 32, color: P, font: F })]
  })
}

function subHeader({ F, P, title }) {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 200, after: 120 },
    children: [new TextRun({ text: title, bold: true, size: 22, color: P, font: F })]
  })
}

function bodyParagraph({ F, text, indent, italics = false }) {
  return new Paragraph({
    spacing: { before: 40, after: 80 },
    indent,
    children: [new TextRun({ text: String(text || ''), italics, color: '475569', font: F, size: 22 })]
  })
}

function bulletList({ F, items }) {
  return new Paragraph({
    spacing: { after: 80 },
    children: [new TextRun({ text: items.map(i => `• ${i}`).join('\n'), font: F, size: 20, color: '2D3748' })]
  })
}

function buildKeyValueTable({ F, P, items, valueFormatter = (v) => String(v) }) {
  const entries = Object.entries(items)
  const rows = entries.map(([k, v], i) => new TableRow({
    children: [
      new TableCell({
        width: { size: Math.floor(CW * 0.5), type: WidthType.DXA },
        shading: { fill: i % 2 === 0 ? 'F8FAFC' : 'FFFFFF', type: ShadingType.CLEAR },
        borders: hBd('E2E8F0'),
        margins: { top: 50, bottom: 50, left: 80, right: 60 },
        children: [new Paragraph({ children: [new TextRun({ text: k.replace(/_/g, ' '), font: F, size: 19, color: '2D3748' })] })]
      }),
      new TableCell({
        width: { size: Math.floor(CW * 0.5), type: WidthType.DXA },
        shading: { fill: i % 2 === 0 ? 'F8FAFC' : 'FFFFFF', type: ShadingType.CLEAR },
        borders: hBd('E2E8F0'),
        margins: { top: 50, bottom: 50, left: 80, right: 60 },
        children: [new Paragraph({ children: [new TextRun({ text: valueFormatter(v), bold: true, font: F, size: 19, color: P })] })]
      })
    ]
  }))
  return new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [Math.floor(CW * 0.5), Math.floor(CW * 0.5)],
    rows
  })
}

function gap(b = 120) { return new Paragraph({ spacing: { before: b, after: 0 }, children: [] }) }
function pageBreak() { return new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }) }
function hBd(color = 'E2E8F0') {
  return {
    top: { style: BorderStyle.SINGLE, size: 1, color },
    bottom: { style: BorderStyle.SINGLE, size: 1, color },
    left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
  }
}
function noBd() {
  const s = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
  return { top: s, bottom: s, left: s, right: s }
}
function strip(c) { return (c || '').replace(/^#/, '') }

function severityHex(level) {
  switch (String(level).toLowerCase()) {
    case 'critical': return '991B1B'
    case 'high': return 'B91C1C'
    case 'medium': return 'B45309'
    case 'low': return '475569'
    default: return '475569'
  }
}

function formatCurrency(v) {
  if (v == null || v === '') return 'Not provided'
  const n = Number(v)
  if (!isFinite(n)) return String(v)
  return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 })
}
